import axios from 'axios';
import { getToken } from '../utility/helpers';
import { decrypt, encrypt } from '../utility/encryption';

export const defaultHeader = {
  Accept: 'application/json',
  'Cache-Control': 'no-cache',
  'Content-Type': 'application/json',
};

export async function request(url, payload, method, header, options = {}) {
  delete defaultHeader.Authorization;

  if (options?.isFormData) {
    delete defaultHeader['Content-Type']; // allow multipart boundary
  } else if (options?.isPdf) {
    defaultHeader['Content-Type'] = 'application/pdf';
  } else if (options?.isCsv) {
    defaultHeader['Content-Type'] = 'text/csv';
  } else {
    defaultHeader['Content-Type'] = 'application/json';
  }

  const requestMethod = method.toLowerCase();
  const persistedToken = getToken();

  if (persistedToken) {
    defaultHeader.Authorization = `Bearer ${persistedToken}`;
  }

  // -------------------------
  // 🔥 FORM DATA WRAPPING LOGIC
  // -------------------------
  let finalData = null;

  if (requestMethod === 'get') {
    // GET never sends a body
    finalData = undefined;
  } else if (options?.isFormData) {
    if (options?.skipWrapping) {
      // Send FormData as-is — backend (multer) expects flat field names
      payload.append('encrypted', 'false');
      finalData = payload;
    } else {
      // Wrap FormData inside another FormData
      const wrapper = new FormData();
      wrapper.append('encrypted', 'false');

      for (const [key, value] of payload.entries()) {
        wrapper.append(`payload[${key}]`, value);
      }

      finalData = wrapper;
    }
  } else {
    // Normal JSON / encrypted JSON
    finalData = options?.shouldEncrypt
      ? { payload: encrypt(payload), encrypted: true }
      : { payload: payload, encrypted: false };
  }

  // -------------------------
  // 🔥 AXIOS REQUEST
  // -------------------------
  return axios
    .request({
      url,
      method: requestMethod,
      headers: { ...defaultHeader, ...header },
      responseType: !options.isDownload && !options.isPdf ? 'json' : 'blob',

      ...(requestMethod === 'get' ? { params: payload } : { data: finalData }),
    })
    .then(({ data }) => data)
    .then((jsonResponse) => {
      // handle encrypted JSON
      if (!jsonResponse.arrayBuffer) {
        if (jsonResponse?.encrypted) {
          const decryptedData = decrypt(jsonResponse?.data);
          return {
            ...jsonResponse,
            data: decryptedData || jsonResponse?.data,
          };
        }

        return jsonResponse;
      }

      // handle file download
      if (options.isDownload) {
        const objectUrl = window.URL.createObjectURL(jsonResponse);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = options.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        return window.URL.createObjectURL(jsonResponse);
      }

      return null;
    })
    .catch((errorResponse) => {
      if (errorResponse.message === 'Network Error') {
        errorResponse.message = 'Network connection error';
      }

      return new Promise((_, reject) => {
        reject(errorResponse.response?.data);
      });
    });
}
